import { useMemo } from 'react';
import { Card, CardBody, Skeleton } from '@vnetwork/ui/primitives';
import { Stat, StatGroup } from '@vnetwork/ui/data';
import { useServiceStatistical, type ServiceStatus } from '@/api/services';

/** One headline stat: which statuses it sums, its label and legend color. */
interface StatDef {
  statuses: ServiceStatus[] | 'total';
  label: string;
  color: string;
}

/** The main-list status band (official services, `is_poc:false`). */
const STAT_DEFS: StatDef[] = [
  { statuses: 'total', label: 'Total', color: 'var(--vn-primary-solid)' },
  { statuses: ['active'], label: 'Active', color: 'var(--vn-success-solid)' },
  { statuses: ['waiting-confirm'], label: 'Waiting confirm', color: 'var(--vn-info-solid)' },
  { statuses: ['waiting-create-service'], label: 'Waiting creation', color: 'var(--vn-info-solid)' },
  { statuses: ['pending-terminated'], label: 'Pending terminated', color: 'var(--vn-warning-solid)' },
  { statuses: ['cancelled'], label: 'Cancelled', color: 'var(--vn-danger-solid)' },
];

/**
 * Service summary band: 6 `Stat` cards (Total · Active · Waiting confirm ·
 * Waiting creation · Pending terminated · Cancelled) aggregated from the
 * `is_poc:false` slice of `/services/statistical`. No period-over-period delta —
 * the endpoint returns plain per-status counts.
 */
export function ServiceStatBand() {
  const { data, isLoading } = useServiceStatistical();

  const counts = useMemo(() => {
    const byStatus = new Map<string, number>();
    let total = 0;
    for (const bucket of data?.results ?? []) {
      if (bucket.is_poc) continue;
      byStatus.set(bucket.service_status, (byStatus.get(bucket.service_status) ?? 0) + bucket.count);
      total += bucket.count;
    }
    return { byStatus, total };
  }, [data]);

  const valueFor = (def: StatDef): number =>
    def.statuses === 'total'
      ? counts.total
      : def.statuses.reduce((sum, s) => sum + (counts.byStatus.get(s) ?? 0), 0);

  return (
    <Card flat style={{ marginBottom: 'var(--vn-space-4)' }}>
      <CardBody>
        {isLoading ? (
          <StatGroup columns={6}>
            {STAT_DEFS.map((def) => (
              <Skeleton key={def.label} style={{ height: 56 }} />
            ))}
          </StatGroup>
        ) : (
          <StatGroup columns={6}>
            {STAT_DEFS.map((def) => (
              <Stat
                key={def.label}
                label={def.label}
                dotColor={def.color}
                value={valueFor(def).toLocaleString('en-US')}
              />
            ))}
          </StatGroup>
        )}
      </CardBody>
    </Card>
  );
}
